const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v0';
const API_PREFIX = `${API_BASE}/api/${API_VERSION}`;

async function request(path, { method = 'POST', body, keepalive = false } = {}) {
  if (!API_BASE) return null;
  try {
    const res = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body == null ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body == null ? undefined : JSON.stringify(body),
      keepalive,
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function snapshotPayload(snapshot = {}) {
  return {
    userId: snapshot.userId || '',
    brand: snapshot.brand || '',
    source: snapshot.source || '',
    device: snapshot.device || 'desktop',
    score: snapshot.score || 0,
    waveNumber: snapshot.clearWave || snapshot.wave || 1,
    wavesCompleted: snapshot.clearWave || snapshot.wave || 0,
    durationMs: snapshot.durationMs,
    bricksDestroyed: snapshot.bricksDestroyed,
    bricksTotal: snapshot.bricksTotal,
    maxCombo: snapshot.maxCombo || snapshot.waveMaxCombo || 0,
    berserkUsed: !!snapshot.berserkUsed,
    selectedBullets: snapshot.selectedBullets || [],
    customPhrases: snapshot.customPhrases || [],
  };
}

export function lookupBrand(brand) {
  const name = String(brand || '').trim();
  if (!name) return Promise.resolve(null);
  return request(`/plays/brand?brand=${encodeURIComponent(name)}`, { method: 'GET' });
}

export function fetchPublicSettings() {
  return request('/settings/public', { method: 'GET' });
}

export function fetchPlayHistory(userId, { limit = 10 } = {}) {
  const id = String(userId || '').trim();
  if (!id) return Promise.resolve(null);
  return request(
    `/plays/history?userId=${encodeURIComponent(id)}&limit=${encodeURIComponent(limit)}`,
    { method: 'GET' },
  );
}

export function startPlay(snapshot) {
  const payload = snapshotPayload(snapshot);
  return request('/plays/start', {
    body: {
      userId: payload.userId,
      brand: payload.brand,
      source: payload.source,
      device: payload.device,
      selectedBullets: payload.selectedBullets,
      customPhrases: payload.customPhrases,
    },
  });
}

export function recordWave(sessionId, snapshot) {
  const payload = snapshotPayload(snapshot);
  return request(`/plays/${sessionId}/wave`, {
    body: {
      waveNumber: payload.waveNumber,
      score: payload.score,
      durationMs: payload.durationMs,
      bricksDestroyed: payload.bricksDestroyed,
      bricksTotal: payload.bricksTotal,
      maxCombo: snapshot.waveMaxCombo || payload.maxCombo,
      berserkUsed: payload.berserkUsed,
    },
  });
}

export function recordMood(sessionId, { moodId, moodLabel, action, ...snapshot }, { keepalive = false } = {}) {
  const payload = snapshotPayload(snapshot);
  return request(`/plays/${sessionId}/mood`, {
    body: {
      waveNumber: payload.waveNumber,
      moodId,
      moodLabel,
      action,
      score: payload.score,
      maxCombo: payload.maxCombo,
      selectedBullets: payload.selectedBullets,
      customPhrases: payload.customPhrases,
    },
    keepalive,
  });
}

export function endPlay(sessionId, snapshot, { keepalive = false } = {}) {
  const payload = snapshotPayload(snapshot);
  const moodId = Number(snapshot.moodId);
  const durationMs = snapshot.sessionDurationMs ?? payload.durationMs;
  return request(`/plays/${sessionId}/end`, {
    body: {
      exitAction: snapshot.exitAction || 'abandoned',
      score: payload.score,
      wavesCompleted: payload.wavesCompleted,
      maxCombo: payload.maxCombo,
      durationMs,
      selectedBullets: payload.selectedBullets,
      customPhrases: payload.customPhrases,
      ...(moodId >= 1 && moodId <= 5
        ? {
            moodId,
            moodLabel: snapshot.moodLabel || '',
            moodAction: snapshot.moodAction || snapshot.action || 'abandoned',
            waveNumber: payload.waveNumber,
          }
        : {}),
    },
    keepalive,
  });
}
